import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from '../../../env.service';

export interface UserTenant {
  tenantId: string;
  role: 'admin' | 'user' | 'Admin' | 'Writer' | 'Reader';
  isOwner?: boolean;
  joined_at?: string;
  target?: {
    _id: string;
    name: string;
  };
}

export interface TenantUserData {
  tenants?: UserTenant[];
  lastActiveTenant?: string;
}
const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private envService = inject(EnvService);
  private http = inject(HttpClient);
  private injector = inject(Injector);
  // Expose the active tenant ID as a signal for reactive UI updates
  public activeTenantId: WritableSignal<string | null> = signal<string | null>(null);

  constructor() {
    // Read synchronously to prevent flashing on load
    const stored = localStorage.getItem('active_workspace');
    if (stored) {
      this.activeTenantId.set(stored);
    }
    // If nothing is stored, leave activeTenantId as null.
    // initializeTenant() will resolve it from user data once available.
  }

  /**
   * Initializes the tenant context using the loaded user data.
   * Priority: localStorage -> user.lastActiveTenant -> user.tenants[0] -> fallback
   */
  public initializeTenant(user: TenantUserData): void {
    // 0. Check URL query parameters for cross-domain workspace switching (?workspace= or ?tenantId=)
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const queryTenant = searchParams.get('workspace') || searchParams.get('tenantId');
      if (queryTenant && this.isValidTenant(user, queryTenant)) {
        localStorage.setItem('active_workspace', queryTenant);
        const cleanUrl = window.location.pathname + (window.location.hash || '');
        window.history.replaceState({}, '', cleanUrl);
      }
    } catch (e) {
      console.error('[TenantService] Failed parsing query params:', e);
    }

    const storedTenant = localStorage.getItem('active_workspace');

    let nextTenant: string | null = null;

    if (storedTenant && this.isValidTenant(user, storedTenant)) {
      nextTenant = storedTenant;
    } else if (user?.lastActiveTenant && this.isValidTenant(user, user.lastActiveTenant)) {
      nextTenant = user.lastActiveTenant;
    } else if (user?.tenants && user.tenants.length > 0) {
      nextTenant = user.tenants[0].tenantId;
    } else {
      // No tenants found on the user — nothing to resolve to
      nextTenant = null;
    }

    // If the next calculated tenant isn't what the app currently thinks is active,
    // we must switch context and reload the app so API retries run nicely on the new tenant.
    if (this.activeTenantId() !== nextTenant) {
      // Clear out stored tenant if it was definitively invalid
      if (storedTenant && !this.isValidTenant(user, storedTenant)) {
        localStorage.removeItem('active_workspace');
      }
      this.switchTenant(nextTenant);
    } else if (nextTenant) {
      this.setActiveTenantLocally(nextTenant);
    }
  }

  /**
   * Switches the active workspace, updates local storage, saves preference to backend,
   * and optionally triggers a page reload to cleanly reset the app context.
   */
  public async switchTenant(tenantId: string | null, skipReload = false): Promise<void> {
    if (!tenantId) return;
    if (this.activeTenantId() === tenantId && !skipReload) return;

    this.setActiveTenantLocally(tenantId);

    // Fire and await updating the backend preference
    // Using fetch here instead of HttpClient to completely avoid Angular Interceptor cyclic dependencies!
    try {
      const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
      const token = loginData?.token || '';

      await fetch(`${this.envService.apiUrl}auth/users/set-active-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-app-name': this.envService.appName,
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({ tenantId }),
      });
    } catch (err) {
      console.error('Failed to sync active tenant preference:', err);
    }

    if (!skipReload) {
      window.location.href = '/';
    }
  }

  /**
   * Internal helper to set signals and localStorage synchronously
   */
  public setActiveTenantLocally(tenantId: string): void {
    localStorage.setItem('active_workspace', tenantId);
    this.activeTenantId.set(tenantId);
  }

  /**
   * Validates if a tenant string exists in the user's allowed array
   */
  private isValidTenant(user: TenantUserData, tenantId: string): boolean {
    if (!user || !user.tenants) return false;
    return user.tenants.some((t) => t.tenantId === tenantId);
  }

  updateTenantAppname(appname: string, workspaceSlug: string): Observable<any> {
    const authService = this.injector.get(AuthService);
    return this.http
      .put(`${this.envService.apiUrl}users/tenant/appname`, { appname, workspaceSlug }, httpOptions)
      .pipe(tap(() => authService.getUserData(null, false).catch((err) => console.error(err))));
  }

  getTenantSettings(): Observable<any> {
    return this.http.get(`${this.envService.apiUrl}users/tenant/settings`, httpOptions);
  }

  updateTenantAiAccess(aiAllowedUsers: string[] | null, aiAccessMode: 'all' | 'restricted'): Observable<any> {
    return this.http.put(`${this.envService.apiUrl}users/tenant/ai-access`, { aiAllowedUsers, aiAccessMode }, httpOptions);
  }

  private _tenantAiSettings = signal<{ aiAllowedUsers: string[] | null; aiAccessMode: 'all' | 'restricted' }>({
    aiAllowedUsers: null,
    aiAccessMode: 'all',
  });
  public readonly tenantAiSettings = this._tenantAiSettings.asReadonly();

  setTenantAiSettings(settings: { aiAllowedUsers: string[] | null; aiAccessMode: 'all' | 'restricted' }) {
    this._tenantAiSettings.set(settings);
  }

  loadTenantAiSettings() {
    this.getTenantSettings().subscribe({
      next: (res) => {
        if (res.ok) {
          this._tenantAiSettings.set({
            aiAllowedUsers: res.aiAllowedUsers || null,
            aiAccessMode: res.aiAccessMode || 'all',
          });
        }
      },
      error: (err) => console.error('Error loading tenant AI settings:', err),
    });
  }
}
